'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  User,
  browserLocalPersistence,
  onAuthStateChanged,
  setPersistence,
} from 'firebase/auth';
import { doc, getDoc, getDocs, collection, query, where, limit } from 'firebase/firestore';

import { auth, db } from '@/lib/firebase.client';
import { ensureUserMembershipStatus } from '@/lib/firestore/memberships';
import type { UserProfile, UserRecord } from '@/lib/firestore/types';

type AuthContextValue = {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  refreshingProfile: boolean;
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshingProfile, setRefreshingProfile] = useState(false);

  useEffect(() => {
    // Ensure Firebase persists sessions locally
    setPersistence(auth, browserLocalPersistence).catch(() => {
      /* ignore */
    });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);
      setLoading(false);

      if (!nextUser) {
        setProfile(null);
        return;
      }

      setRefreshingProfile(true);
      try {
        const userRecord = await fetchUserRecord(nextUser.uid, nextUser.email ?? undefined);
        const profile = toUserProfile(nextUser.uid, userRecord, nextUser.email);
        setProfile(profile);
        logProfile(userRecord ? 'initial-load' : 'initial-load-missing-doc', profile);
      } catch (error) {
        console.error('Failed to load user profile', error);
        const profile = toUserProfile(nextUser.uid, null, nextUser.email);
        setProfile(profile);
        logProfile('initial-load-error', profile);
      } finally {
        setRefreshingProfile(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      return;
    }
    setRefreshingProfile(true);
    try {
      const userRecord = await fetchUserRecord(user.uid, user.email ?? undefined);
      const refreshed = toUserProfile(user.uid, userRecord, user.email);
      setProfile(refreshed);
      logProfile('refresh', refreshed);
    } catch (error) {
      console.error('Unable to refresh profile', error);
    } finally {
      setRefreshingProfile(false);
    }
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      profile,
      loading,
      refreshingProfile,
      refreshProfile,
    }),
    [loading, profile, refreshingProfile, refreshProfile, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

const toUserProfile = (uid: string, data: UserRecord | null, fallbackEmail?: string | null): UserProfile => {
  const normalized = ensureUserMembershipStatus({
    ...(data ?? {}),
    email: data?.email ?? fallbackEmail ?? undefined,
    uid,
    id: uid,
  });

  const membership = normalized.membership ?? {};
  return {
    ...normalized,
    account_type: normalized.account_type ?? 'uncommon',
    membership_id: normalized.membership_id ?? membership.membership_id ?? 'membership_uncommon',
    membership: {
      membership_id: membership.membership_id ?? 'membership_uncommon',
      account_type: membership.account_type ?? normalized.account_type ?? 'uncommon',
      ...membership,
    },
  };
};

async function fetchUserRecord(uid: string, email?: string | null): Promise<UserRecord | null> {
  const directSnap = await getDoc(doc(db, 'users', uid));
  if (directSnap.exists()) {
    return directSnap.data() as UserRecord;
  }

  const usersCollection = collection(db, 'users');
  const byUidQuery = query(usersCollection, where('uid', '==', uid), limit(1));
  const byUidSnap = await getDocs(byUidQuery);
  if (!byUidSnap.empty) {
    return byUidSnap.docs[0].data() as UserRecord;
  }

  if (email) {
    const byEmailQuery = query(usersCollection, where('email', '==', email), limit(1));
    const byEmailSnap = await getDocs(byEmailQuery);
    if (!byEmailSnap.empty) {
      return byEmailSnap.docs[0].data() as UserRecord;
    }
  }

  return null;
}

function logProfile(event: string, profile: UserProfile | null) {
  if (typeof window === 'undefined' || !profile) return;
   
  console.groupCollapsed(`[AuthProvider] ${event}`);
   
  console.table([
    {
      uid: profile.uid,
      email: profile.email ?? null,
      hasValidMembership: profile.hasValidMembership ?? null,
      start_date: profile.membership?.start_date ?? null,
      duration_month: profile.membership?.duration_month ?? null,
      duration_month_free: profile.membership?.duration_month_free ?? null,
      transactionId: profile.membership?.transactionId ?? null,
      next_payment_date: profile.membership?.next_payment_date ?? null,
    },
  ]);
   
  console.dir(profile.membership ?? null, { depth: null });
   
  console.groupEnd();
}
